/**
 * CommentsSection.tsx
 * 
 * Componente per la gestione dei commenti delle richieste nell'applicazione Ufficio Stile.
 * Supporta l'aggiunta, modifica, eliminazione e visualizzazione gerarchica dei commenti.
 * Include funzionalità di reply e diversi tipi di commenti (generale, issue, suggestion).
 */

import React, { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Button,
  Card,
  ActionIcon,
  Badge,
  Box,
  Textarea,
  Select,
  Avatar,
  Divider,
  Alert,
  Modal,
  Menu,
  Tabs,
  Checkbox
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconMessage,
  IconSend,
  IconEdit,
  IconTrash,
  IconCornerDownRight,
  IconDots,
  IconAlertCircle,
  IconBulb,
  IconMessageCircle,
  IconCheck,
  IconX,
  IconGlobe,
  IconRefresh
} from '@tabler/icons-react';
import {
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useCommentsByRequest
} from '../../../hooks';
import {
  useAddGlobalComment,
} from '../../../hooks/useCrossPortalHooksClean';
import { Comment, CommentVisibility, CommentCreateRequest, CommentUpdateRequest } from '../../../types/api';
import { useOktaAuth } from '../../../providers/okta-auth-provider';
import type { GlobalComment } from '../../../services/api/comments-new';
import { formatDate } from '../../../lib/utils';

interface CommentsSectionProps {
  requestId: string;
  crossPortalRequestId?: string;
  comments?: Comment[];
  readOnly?: boolean;
}

interface CommentItemProps {
  comment: Comment;
  onReply: (parentId: string) => void;
  onEdit: (comment: Comment) => void;
  onDelete: (commentId: string) => void;
  readOnly?: boolean;
  level?: number; // Per gestire la nidificazione
}

export function CommentsSection({ 
  requestId, 
  crossPortalRequestId,
  comments: commentsProp = [], 
  readOnly = false 
}: CommentsSectionProps) {
  const { userInfo } = useOktaAuth();
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('local');
  const [shareGlobally, setShareGlobally] = useState(false);

  // Fetch comments from API (local + cross-portal in un'unica chiamata)
  const {
    data: fetchedData,
    isLoading: loadingComments,
    refetch: refetchComments
  } = useCommentsByRequest(requestId);

  // Estrai commenti locali e globali dalla risposta combinata
  const fetchedComments = fetchedData?.comments ?? [];
  const fetchedGlobalComments: GlobalComment[] = fetchedData?.globalComments ?? [];

  // Use fetched comments if available, otherwise use props
  const commentsBeforeDedup = fetchedComments.length > 0 ? fetchedComments : commentsProp;

  // Il backend può salvare più copie identiche dello stesso commento cross-portal
  // (nessun controllo di idempotenza in fase di import/echo): nascondiamo i
  // duplicati esatti senza toccare contenuto/autore. Vedi src/utils/
  // (rimosso: un tentativo precedente riscriveva anche il testo ed era sbagliato).
  const comments = removeDuplicates(
    commentsBeforeDedup,
    (comment) => `${comment.authorName ?? ''}|${comment.visibility}|${normalizeText(comment.content)}`
  );
  const globalComments = removeDuplicates(
    fetchedGlobalComments,
    (comment) => `${comment.createdByName ?? ''}|${comment.sourcePortal ?? ''}|${normalizeText(comment.content)}`
  );

  const createMutation = useCreateComment();
  const updateMutation = useUpdateComment();
  const deleteMutation = useDeleteComment();

  // Cross-portal hook per creazione commenti globali
  const addGlobalCommentMutation = useAddGlobalComment();

  // Form per nuovo commento
  const newCommentForm = useForm({
    initialValues: {
      content: '',
      visibility: CommentVisibility.RequestLevel as CommentVisibility
    },
    validate: {
      content: (value) => value.trim().length < 3 ? 'Il commento deve essere di almeno 3 caratteri' : null
    }
  });

  // Form per modifica commento
  const editCommentForm = useForm({
    initialValues: {
      content: '',
      visibility: CommentVisibility.RequestLevel as CommentVisibility
    }
  });

  // Gestione invio nuovo commento
  const handleSubmitComment = async (values: typeof newCommentForm.values) => {
    if (readOnly) return;

    try {
      if (shareGlobally && crossPortalRequestId) {
        // Invio come commento globale
        await addGlobalCommentMutation.mutateAsync({
          content: values.content.trim(),
          crossPortalRequestId: crossPortalRequestId ? parseInt(crossPortalRequestId as any) : undefined,
          authorName: userInfo?.name || userInfo?.displayName || `${userInfo?.firstName} ${userInfo?.lastName}`,
          createdBy: userInfo?.email,
          sourcePortal: 'UfficioStile'
        });

        await refetchComments();
      } else {
        // Invio come commento locale
        const commentData: CommentCreateRequest = {
          content: values.content.trim(),
          visibility: values.visibility,
          requestId,
          parentCommentId: replyToId || undefined
        };

        await createMutation.mutateAsync(commentData);
        
        // Refetch comments to show the new one
        await refetchComments();

        notifications.show({
          title: 'Commento aggiunto',
          message: 'Il commento è stato aggiunto con successo',
          color: 'green'
        });
      }

      newCommentForm.reset();
      setReplyToId(null);
      setShareGlobally(false);

    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'aggiunta del commento',
        color: 'red'
      });
    }
  };

  // Gestione modifica commento
  const handleEditComment = async (values: typeof editCommentForm.values) => {
    if (!editingComment) return;

    try {
      const updateData: CommentUpdateRequest = {
        content: values.content.trim(),
        visibility: values.visibility
      };

      await updateMutation.mutateAsync({
        id: editingComment.id,
        comment: updateData
      });
      
      // Refetch comments to show the updated one
      await refetchComments();

      setEditingComment(null);
      editCommentForm.reset();

      notifications.show({
        title: 'Commento aggiornato',
        message: 'Il commento è stato aggiornato con successo',
        color: 'green',
        icon: <IconCheck size={16} />
      });
    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'aggiornamento del commento',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  // Gestione eliminazione commento
  const handleDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteMutation.mutateAsync(commentToDelete);
      
      // Refetch comments to update the list
      await refetchComments();

      setDeleteModalOpen(false);
      setCommentToDelete(null);

      notifications.show({
        title: 'Commento eliminato',
        message: 'Il commento è stato eliminato con successo',
        color: 'green',
        icon: <IconCheck size={16} />
      });
    } catch (error) {
      notifications.show({
        title: 'Errore',
        message: 'Errore durante l\'eliminazione del commento',
        color: 'red',
        icon: <IconAlertCircle size={16} />
      });
    }
  };

  // Gestione reply
  const handleReply = (parentId: string) => {
    setReplyToId(parentId);
    setEditingComment(null);
  };

  // Gestione edit
  const handleEdit = (comment: Comment) => {
    setEditingComment(comment);
    setReplyToId(null);
    editCommentForm.setValues({
      content: comment.content,
      visibility: comment.visibility
    });
  };

  // Gestione delete
  const handleDelete = (commentId: string) => {
    setCommentToDelete(commentId);
    setDeleteModalOpen(true);
  };

  // Utility functions
  const getCommentVisibilityIcon = (visibility: CommentVisibility) => {
    switch (visibility) {
      case CommentVisibility.TaskOnly: return <IconAlertCircle size={16} color="red" />;
      case CommentVisibility.ExternalPortal: return <IconBulb size={16} color="yellow" />;
      default: return <IconMessageCircle size={16} color="blue" />;
    }
  };

  const getCommentVisibilityColor = (visibility: CommentVisibility) => {
    switch (visibility) {
      case CommentVisibility.TaskOnly: return 'red';
      case CommentVisibility.ExternalPortal: return 'yellow';
      default: return 'blue';
    }
  };

  const getCommentVisibilityLabel = (visibility: CommentVisibility) => {
    switch (visibility) {
      case CommentVisibility.TaskOnly: return 'Solo Task';
      case CommentVisibility.ExternalPortal: return 'Portale Esterno';
      default: return 'Livello Richiesta';
    }
  };

  // Organizza commenti in struttura gerarchica
  const organizeComments = (comments: Comment[]): Comment[] => {
    const commentMap = new Map<string, Comment>();
    const rootComments: Comment[] = [];

    // Prima passata: crea mappa e identifica commenti root
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
      if (!comment.parentCommentId) {
        rootComments.push(commentMap.get(comment.id)!);
      }
    });

    // Seconda passata: costruisce gerarchia
    comments.forEach(comment => {
      if (comment.parentCommentId) {
        const parent = commentMap.get(comment.parentCommentId);
        const child = commentMap.get(comment.id);
        if (parent && child) {
          parent.replies = parent.replies || [];
          parent.replies.push(child);
        }
      }
    });

    return rootComments.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  };

  const organizedComments = organizeComments(comments);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={500} size="lg">Commenti</Text>
        <Badge variant="light" color="blue">
          {comments.length + globalComments.length} commento{(comments.length + globalComments.length) !== 1 ? 'i' : ''}
        </Badge>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="local" leftSection={<IconMessageCircle size={16} />}>
            Commenti Interni ({comments.length})
          </Tabs.Tab>
          <Tabs.Tab value="global" leftSection={<IconGlobe size={16} />}>
            Commenti Condivisi ({globalComments.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="local">
          <Stack gap="md" mt="md">
            {/* Form Nuovo Commento */}
            {!readOnly && (
              <Card withBorder padding="md">
                <form onSubmit={newCommentForm.onSubmit(handleSubmitComment)}>
                  <Stack gap="sm">
                    {replyToId && (
                      <Alert color="blue" variant="light">
                        <Group justify="space-between">
                          <Text size="sm">Stai rispondendo a un commento</Text>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => setReplyToId(null)}
                          >
                            <IconX size={12} />
                          </ActionIcon>
                        </Group>
                      </Alert>
                    )}

                    <Group>
                      <Checkbox
                        checked={shareGlobally}
                        onChange={(event) => setShareGlobally(event.currentTarget.checked)}
                        label="Condividi con tutti i portali"
                      />
                    </Group>

                    {!shareGlobally && (
                      <Group>
                        <Select
                          data={[
                            { value: CommentVisibility.RequestLevel.toString(), label: 'Livello Richiesta' },
                            { value: CommentVisibility.TaskOnly.toString(), label: 'Solo Task' },
                            { value: CommentVisibility.ExternalPortal.toString(), label: 'Portale Esterno' }
                          ]}
                          w={150}
                          {...newCommentForm.getInputProps('visibility')}
                          value={newCommentForm.values.visibility.toString()}
                          onChange={(value) => newCommentForm.setFieldValue('visibility', value ? parseInt(value) as CommentVisibility : CommentVisibility.RequestLevel)}
                        />
                      </Group>
                    )}

                    <Textarea
                      placeholder="Scrivi un commento..."
                      minRows={3}
                      {...newCommentForm.getInputProps('content')}
                    />

                    <Group justify="flex-end">
                      {replyToId && (
                        <Button
                          variant="light"
                          onClick={() => setReplyToId(null)}
                        >
                          Annulla
                        </Button>
                      )}
                      <Button
                        type="submit"
                        leftSection={<IconSend size={16} />}
                        loading={createMutation.isPending || addGlobalCommentMutation.isPending}
                      >
                        {shareGlobally ? 'Condividi' : (replyToId ? 'Rispondi' : 'Commenta')}
                      </Button>
                    </Group>
                  </Stack>
                </form>
              </Card>
            )}

            {/* Lista Commenti Locali */}
            <Stack gap="sm">
              {organizedComments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onReply={handleReply}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  readOnly={readOnly}
                  level={0}
                />
              ))}

              {comments.length === 0 && (
                <Alert color="gray" variant="light">
                  <Text size="sm" c="dimmed" ta="center">
                    Nessun commento interno presente
                  </Text>
                </Alert>
              )}
            </Stack>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="global">
          <Stack gap="md" mt="md">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Commenti visibili in tutti i portali (Personalizzati, Ufficio Stile, Configuratore)
              </Text>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRefresh size={14} />}
                onClick={() => refetchComments()}
              >
                Sincronizza
              </Button>
            </Group>

            <Stack gap="sm">
              {globalComments.map((comment) => (
                <Card key={comment.id} withBorder padding="md">
                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Group>
                        <Avatar size="sm" color="blue">
                          {comment.createdByName?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Group gap="xs">
                            <Text fw={500} size="sm">
                              {comment.createdByName || 'Utente'}
                            </Text>
                            {comment.sourcePortal && (
                              <Badge size="xs" variant="light" color="orange">
                                Da {comment.sourcePortal}
                              </Badge>
                            )}
                          </Group>
                          <Text size="xs" c="dimmed">
                            {formatDate(comment.createdAt)}
                          </Text>
                        </Box>
                      </Group>
                    </Group>

                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {comment.content}
                    </Text>
                  </Stack>
                </Card>
              ))}

              {globalComments.length === 0 && (
                <Alert color="gray" variant="light">
                  <Text size="sm" c="dimmed" ta="center">
                    Nessun commento condiviso disponibile
                  </Text>
                </Alert>
              )}
            </Stack>
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Modal Modifica Commento - mantengo il codice esistente */}
      <Modal
        opened={!!editingComment}
        onClose={() => {
          setEditingComment(null);
          editCommentForm.reset();
        }}
        title="Modifica Commento"
        size="md"
      >
        <form onSubmit={editCommentForm.onSubmit(handleEditComment)}>
          <Stack gap="md">
            <Select
              label="Visibilità"
              data={[
                { value: CommentVisibility.RequestLevel.toString(), label: 'Livello Richiesta' },
                { value: CommentVisibility.TaskOnly.toString(), label: 'Solo Task' },
                { value: CommentVisibility.ExternalPortal.toString(), label: 'Portale Esterno' }
              ]}
              {...editCommentForm.getInputProps('visibility')}
              value={editCommentForm.values.visibility.toString()}
              onChange={(value) => editCommentForm.setFieldValue('visibility', value ? parseInt(value) as CommentVisibility : CommentVisibility.RequestLevel)}
            />

            <Textarea
              label="Contenuto"
              minRows={4}
              {...editCommentForm.getInputProps('content')}
            />

            <Group justify="flex-end">
              <Button
                variant="light"
                onClick={() => {
                  setEditingComment(null);
                  editCommentForm.reset();
                }}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                loading={updateMutation.isPending}
              >
                Salva Modifiche
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Modal Conferma Eliminazione */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCommentToDelete(null);
        }}
        title="Conferma Eliminazione"
        size="sm"
      >
        <Stack gap="md">
          <Text>Sei sicuro di voler eliminare questo commento? L'azione non può essere annullata.</Text>
          
          <Group justify="flex-end">
            <Button
              variant="light"
              onClick={() => {
                setDeleteModalOpen(false);
                setCommentToDelete(null);
              }}
            >
              Annulla
            </Button>
            <Button
              color="red"
              onClick={handleDeleteComment}
              loading={deleteMutation.isPending}
            >
              Elimina
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// Componente per singolo commento (supporta nidificazione)
function CommentItem({ comment, onReply, onEdit, onDelete, readOnly = false, level = 0 }: CommentItemProps) {
  const maxLevel = 3; // Massimo livello di nidificazione
  const indent = level * 20;

  return (
    <Card 
      withBorder 
      padding="md" 
      style={{ marginLeft: indent }}
      className={level > 0 ? 'reply-comment' : ''}
    >
      <Stack gap="sm">
        {/* Header Commento */}
        <Group justify="space-between">
          <Group>
            <Avatar size="sm" color="blue">
              {comment.authorName?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box>
              <Group gap="xs">
                <Text fw={500} size="sm">
                  {comment.authorName || 'Utente'}
                </Text>
                <Badge 
                  size="xs" 
                  variant="light" 
                  color={getCommentVisibilityColor(comment.visibility)}
                  leftSection={getCommentVisibilityIcon(comment.visibility)}
                >
                  {getCommentVisibilityLabel(comment.visibility)}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {formatDate(comment.createdAt)}
                {comment.updatedAt !== comment.createdAt && ' (modificato)'}
              </Text>
            </Box>
          </Group>

          {!readOnly && (
            <Menu shadow="md" width={150}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm">
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {level < maxLevel && (
                  <Menu.Item
                    leftSection={<IconCornerDownRight size={14} />}
                    onClick={() => onReply(comment.id)}
                  >
                    Rispondi
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={<IconEdit size={14} />}
                  onClick={() => onEdit(comment)}
                >
                  Modifica
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconTrash size={14} />}
                  color="red"
                  onClick={() => onDelete(comment.id)}
                >
                  Elimina
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        {/* Contenuto Commento */}
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {comment.content}
        </Text>

        {/* Risposte */}
        {comment.replies && comment.replies.length > 0 && (
          <Stack gap="sm" mt="sm">
            <Divider size="xs" />
            {comment.replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                readOnly={readOnly}
                level={level + 1}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

// Helper functions (definite fuori dal componente per riutilizzo)
function getCommentVisibilityIcon(visibility: CommentVisibility) {
  switch (visibility) {
    case CommentVisibility.TaskOnly: return <IconAlertCircle size={16} />;
    case CommentVisibility.ExternalPortal: return <IconBulb size={16} />;
    default: return <IconMessageCircle size={16} />;
  }
}

function getCommentVisibilityColor(visibility: CommentVisibility) {
  switch (visibility) {
    case CommentVisibility.TaskOnly: return 'red';
    case CommentVisibility.ExternalPortal: return 'yellow';
    default: return 'blue';
  }
}

function getCommentVisibilityLabel(visibility: CommentVisibility) {
  switch (visibility) {
    case CommentVisibility.TaskOnly: return 'Solo Task';
    case CommentVisibility.ExternalPortal: return 'Portale Esterno';
    default: return 'Livello Richiesta';
  }
}

// Nasconde le copie esatte di un elemento (stessa chiave), mantenendo la prima
// occorrenza e l'ordine originale. Non modifica il contenuto degli elementi.
function removeDuplicates<T>(items: T[], getKey: (item: T) => string): T[] {
  const seen = new Set<string>();
  return items.filter(item => {
    const key = getKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Normalizza gli spazi (spazi multipli, a capo) per confrontare due testi
// senza dover essere identici byte per byte.
function normalizeText(text?: string): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}
